import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { formatFileSize } from "../../pages/filesManager/pages/createFile/components/methos";
import { UserCreator } from "../../pages/filesManager/pages/detail/components/file/types";
import { Roles } from "../../utils/enums";
import { FileDataResponse, ResponseFilesList, ResponseUserFilesList, ResponseUserWithFilesList, UserFiles } from "./response";
import { Constants } from "../../utils/constants";

dayjs.extend(customParseFormat);

const formatFilesList = ({ response }: { response: ResponseFilesList }) => {

    const row = !response.errors
        ? response?.items.map((item) => {

            const uniqueAreas = Array.from(
                new Map(
                    (item.areasDptos ?? [])
                        .filter(a => a.areaId != null)
                        .map(a => [a.areaId, a.areaName])
                ).values()
            );

            const uniqueDepartments = Array.from(
                new Map(
                    (item.areasDptos ?? [])
                        .filter(d => d.departmentId != null)
                        .map(d => [d.departmentId, d.departmentName])
                ).values()
            );

            return {
                id: item.id,
                area: uniqueAreas, // item.areasDptos?.map(area => area.areaName),
                department: uniqueDepartments, // item.areasDptos?.map(department => department.departmentName),
                fileName: item.fileName,
                createTime: item.createTime,
                solicitedUsers: item.userfilesRequest.map(user => `${user.firstName} ${user.lastName}`),
                type: item.fileType,
                status: item.statusId,
                size: formatFileSize(item.size), //item.size,
                createdBy: item.createdBy,
            }
        })
        : []

    return {
        row
    }
}

const formatUserWithFilesList = ({ response }: { response: ResponseUserWithFilesList }) => {

    const row = !response.errors
        ? response?.items.map((item) => {

            const uniqueAreas = Array.from(
                new Map(
                    (item.userDepartments ?? [])
                        .filter(a => a.areaId != null)
                        .map(a => [a.areaId, a.areaName])
                ).values()
            );

            const uniqueDepartments = Array.from(
                new Map(
                    (item.userDepartments ?? [])
                        .filter(d => d.departmentId != null)
                        .map(d => [d.departmentId, d.departmentName])
                ).values()
            );

            return {
                id: item.id,
                firstName: `${item.firstName} ${item.lastName}`,
                role: item.role.description,
                areaName: uniqueAreas, // item.areasDptos?.map(area => area.areaName),
                departmentName: uniqueDepartments, // item.areasDptos?.map(department => department.departmentName),
                userType: item.userType?.name ?? 'N/A',
            }
        })
        : []

    return {
        row
    }
}

const formatUserFilesList = ({ response, userId, userRole }: { response: ResponseUserFilesList, userId: number; userRole: Roles }) => {

    const row = !response.errors
        ? response?.items.map((item) => {

            return {
                id: item.fileId,
                fileName: item.fileName,
                createTime: item.createTime,
                dateEnd: item.dateEnd,
                size: item.size,
                seeFile: item.canView == 1,
                seeFileIcon: item.canView == 1,
                editable: userRole == Roles.ADMIN || userId == item.createdBy,
                downloadFile: item.canDownload == 1,
                downloadFileIcon: item.canDownload == 1,
                fileType: item.fileType,
                createdBy: item.createdBy,
                canSee: item.canViewUserLogged
            }
        })
        : []

    return {
        row
    }
}

const formatUserFilesToAddList = ({ response }: { response: ResponseUserFilesList }) => {

    const row = !response.errors
        ? response?.items.map((item) => {

            return {
                id: item.fileId,
                fileName: item.fileName,
                createTime: item.createTime,
                expirationDate: item.dateEnd,
                size: item.size,
                seeFile: item.canView == 1,
                seeFileIcon: item.canView == 1,
                editable: true,
                downloadFile: item.canDownload == 1,
                downloadFileIcon: item.canDownload == 1,
                type: item.fileType,
                createdBy: item.createdBy
            }
        })
        : []

    return {
        row
    }
}

const formatFileDetail = ({ response, userId, role }: { response: FileDataResponse, userId: number, role: number }) => {

    const uniqueAreas = Array.from(
        new Map(
            (response.userCreator.userDepartments ?? [])
                .filter(a => a.areaId != null)
                .map(a => [a.areaId, a.areaName])
        ).values()
    );

    const uniqueDepartments = Array.from(
        new Map(
            (response.userCreator.userDepartments ?? [])
                .filter(d => d.departmentId != null)
                .map(d => [d.departmentId, d.departmentName])
        ).values()
    );

    const userCreator: UserCreator = {
        area: uniqueAreas,
        department: uniqueDepartments,
        name: `${response.userCreator.firstName} ${response.userCreator.lastName}`,
        role: response.userCreator.role.description,
        userType: response.userCreator.userType?.name ?? "N/A"
    }

    const solictedUser = response.userfilesRequest.map(item => ({
        id: item.id,
        name: `${item.firstName} ${item.lastName}`
    }))

    const file = {
        createDate: response.createTime,
        editDate: response.updateTime,
        name: response.fileName,
        size: response.size,
        type: response.fileType,
        url: response.downloadUrl ?? ''
    }

    const status = response.statusId;

    const data = {
        from: response.dateStart ? dayjs(response.dateStart, Constants.dayjsFormat) : undefined,
        to: response.dateEnd ? dayjs(response.dateEnd, Constants.dayjsFormat) : undefined
    }

    const key = `${response.createdBy}/${response.fileName}`

    const showOptions = userId == response.createdBy || role == Roles.ADMIN;

    const canDownload = !!response.downloadUrl

    const canSee = response.canViewUserLogged

    const requestingUsers = response.userfilesRequest.map(item => item.id)


    return {
        userCreator,
        solictedUser,
        status,
        file,
        data,
        showOptions,
        canDownload,
        requestingUsers,
        key,
        canSee
    }
}

const formatListUsersPermissions = ({ response }: { response: UserFiles }) => {

    const row = response.map(item => {
        const userDepartments = item.userDepartments ?? [];

        const areaName = userDepartments
            .filter(a => a.areaId != null)
            .map(a => a.areaName)
            .join(', ');

        const departmentName = userDepartments
            .filter(d => d.departmentId != null)
            .map(d => d.departmentName)
            .join(', ');

        return {
            name: `${item.firstName} ${item.lastName}`,
            roleName: item.role.description,
            areaName,
            departmentName,
            userTypeDescription: item.userType?.name ?? 'N/A',
            seeFile: item.filePermissions.canView === 1,
            downloadFile: item.filePermissions.canDownload === 1
        };
    });

    return { row };
}

const formatListAlreadyUsersPermissions = ({ response }: { response: UserFiles }) => {

    const row = response.map(item => {
        const userDepartments = item.userDepartments ?? [];

        const areaName = userDepartments
            .filter(a => a.areaId != null)
            .map(a => a.areaName)
            .join(', ');

        const departmentName = userDepartments
            .filter(d => d.departmentId != null)
            .map(d => d.departmentName)
            .join(', ');

        const areaIds = userDepartments
            .filter(a => a.areaId != null)
            .map(a => a.areaId);

        const departmentIds = userDepartments
            .filter(d => d.departmentId != null)
            .map(d => d.departmentId);

        return {
            seeFile: item.filePermissions.canView === 1,
            downloadFile: item.filePermissions.canDownload === 1,
            userId: item.id,
            firstName: item.firstName,
            lastName: item.lastName,
            fullName: `${item.firstName} ${item.lastName}`,
            area: areaName,
            department: departmentName,
            areaId: areaIds,
            departmentId: departmentIds,
            role: item.role.description,
            roleId: item.roleId,
            userTypeDescription: item.userType?.name ?? 'N/A',
            userType: item.userTypeId
        };
    });

    return { row };
}

export { formatListAlreadyUsersPermissions, formatListUsersPermissions, formatFilesList, formatUserWithFilesList, formatUserFilesList, formatUserFilesToAddList, formatFileDetail }